/**
 * Hook for generating AI monthly billing clinical summaries.
 *
 * Mirrors useGeneratePatientSummary:
 * - Development: Vite proxy adds the API key server-side
 * - Staging/Production: Direct API Gateway call with API key
 */

import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";
import type { GenerateBillingSummaryResponse } from "@benchmark/core/types";

/** Result from useGenerateBillingSummary hook. @agentic connections */
export type UseGenerateBillingSummaryResult = {
  generateSummary: (
    patientId: string,
    billingMonth: string,
  ) => Promise<GenerateBillingSummaryResponse | null>;
  isGenerating: boolean;
  error: Error | null;
};

/**
 * Hook to generate an AI monthly billing clinical summary via API.
 * @agentic connections
 */
export const useGenerateBillingSummary =
  (): UseGenerateBillingSummaryResult => {
    const medplum = useMedplum();
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const generateSummary = useCallback(
      async (
        patientId: string,
        billingMonth: string,
      ): Promise<GenerateBillingSummaryResponse | null> => {
        if (!patientId || !billingMonth) {
          const err = new Error("Patient ID and billing month are required");
          setError(err);
          return null;
        }

        setIsGenerating(true);
        setError(null);

        try {
          const accessToken = await ensureActiveSession(medplum);

          const apiUrl = getApiEndpoint("/api/v1/billing-summary");

          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Medplum-Authorization": `Bearer ${accessToken}`,
              ...getApiAuthHeaders(),
            },
            body: JSON.stringify({ patientId, billingMonth }),
            signal: controller.signal,
          }).finally(() => clearTimeout(timeout));

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            let errorData: Record<string, unknown> = {};
            try {
              errorData = JSON.parse(errorText);
            } catch {
              errorData = { raw: errorText };
            }
            const errorMessage =
              (typeof errorData.error === "string" ? errorData.error : null) ||
              (typeof errorData.message === "string"
                ? errorData.message
                : null) ||
              `Failed to generate summary: ${response.status} ${response.statusText}`;
            throw new Error(errorMessage);
          }

          const data: GenerateBillingSummaryResponse = await response.json();
          setIsGenerating(false);
          return data;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to generate summary. Please try again.";

          const errorObj = new Error(errorMessage);
          setError(errorObj);
          setIsGenerating(false);
          return null;
        }
      },
      [medplum],
    );

    return {
      generateSummary,
      isGenerating,
      error,
    };
  };
